export async function fetchTips() {
  const response = await fetch('/api/tips');
  if (!response.ok) throw new Error('Failed to fetch tips');
  return response.json();
}

export async function fetchPremiumTips() {
  const response = await fetch('/api/tips/premium', {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to fetch premium tips');
  return response.json();
}

export async function fetchPremiumPreview() {
  const response = await fetch('/api/tips/premium-preview');
  if (!response.ok) throw new Error('Failed to fetch premium preview');
  return response.json();
}

export async function refreshPredictions() {
  const response = await fetch('/api/tips/refresh', {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to refresh predictions');
  return response.json();
}

export async function submitPaymentRequest(data: {
  email: string;
  paymentMethod: string;
  receiptImage: string;
}) {
  const response = await fetch('/api/payment-request', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to submit payment request');
  }

  return response.json();
}

// Authentication API functions
export async function login(email: string, password: string) {
  const response = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Login failed');
  }

  return response.json();
}

export async function register(email: string, password: string) {
  const response = await fetch('/api/auth/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Registration failed');
  }

  return response.json();
}

export async function logout() {
  const response = await fetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) throw new Error('Logout failed');
  return response.json();
}

export async function getCurrentUser() {
  const response = await fetch('/api/auth/me', {
    credentials: 'include',
  });

  if (response.status === 401) return null;
  if (!response.ok) throw new Error('Failed to fetch user');
  return response.json();
}

// Telegram API functions
import type { TelegramAccountStatus } from '@shared/types';

export async function getTelegramStatus(): Promise<TelegramAccountStatus> {
  const response = await fetch('/api/auth/telegram/status', {
    credentials: 'include',
  });

  if (response.status === 401) {
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    throw new Error('Failed to fetch Telegram status');
  }

  return response.json();
}

export async function unlinkTelegram(): Promise<void> {
  const response = await fetch('/api/auth/telegram/link', {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to unlink Telegram');
  }
}

export async function updateTelegramNotifications(enabled: boolean): Promise<void> {
  const response = await fetch('/api/auth/telegram/notifications', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ notificationsEnabled: enabled }),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update notification settings');
  }
}
